import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import get from 'lodash/get';
import { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import Badge from '@/modules/common/components/Badge.tsx';
import OverdueTaskBadge from '@/modules/common/components/OverdueTaskBadge.tsx';
import { checkIfTaskAssignedToUser } from '@/modules/common/helpers/checkIfTaskAssignedToUser.ts';
import { checkIfTaskOverdueSoon } from '@/modules/common/helpers/checkIfTaskOverdueSoon.ts';
import { normalizeTaskDueDate } from '@/modules/common/helpers/normalizeTaskDueDate.ts';
import { useTasksListQuery } from '@/modules/common/queries/tasks.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import UsersAvatarsSet from '@/modules/admin-dashboard/components/UsersAvatarsSet.tsx';
import { CreateTaskModal } from '@/modules/admin-dashboard/components/modals/CreateTaskModal.tsx';
import { UpdateTaskModal } from '@/modules/admin-dashboard/components/modals/UpdateTaskModal.tsx';
import { Button, InfoCard, ModalProviderContext, Skeleton } from 'ui';
import { PlusIcon } from 'ui/icons';
import { TaskListMode, TaskListWithRelationsResponse, TaskResult } from 'compliance-api';

const UpcomingTasksSkeleton = () => {
  return (
    <div className="flex flex-col gap-8">
      {Array(5)
        .fill(null)
        .map((_, i) => (
          <div key={i} className="flex justify-between">
            <div className="flex flex-col gap-2.5">
              <Skeleton width={91} height={13} />
              <Skeleton width={162} height={11} />
            </div>

            <div className="flex -space-x-1.5">
              <Skeleton
                width={36}
                height={36}
                borderRadius={36}
                className="z-20 border border-white"
              />
              <Skeleton
                width={36}
                height={36}
                borderRadius={36}
                className="z-10 border border-white"
              />
              <Skeleton width={36} height={36} borderRadius={36} className="border border-white" />
            </div>
          </div>
        ))}
    </div>
  );
};

interface UpcomingTasksProps {
  creatable?: boolean;
  viewAllPath: string;
}

const UpcomingTasks = ({ creatable = false, viewAllPath }: UpcomingTasksProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { data: currentUserData } = useCurrentUserQuery();
  const isUserAdmin = rolesManipulator.current_user_role === Role.COMPLIANCE_ADMIN;

  const { data, isLoading } = useTasksListQuery({
    page: 1,
    page_size: 1000,
    mode: TaskListMode.Todo,
  });

  const tasks = get(data, 'pages', [])
    .flatMap(({ data }) => data)
    .slice(0, 5);

  const handleOpenCreateTaskModal = useCallback(() => {
    openModal(CreateTaskModal);
  }, [openModal]);

  const handleOpenUpdateTaskModal = useCallback(
    (task: TaskListWithRelationsResponse) => {
      openModal(UpdateTaskModal, {
        defaultDisabled: true,
        id: task?.id,
      });
    },
    [openModal],
  );

  const headerAction =
    creatable &&
    (isLoading ? (
      <Skeleton width={15} height={15} borderRadius={15} />
    ) : (
      <button onClick={handleOpenCreateTaskModal}>
        <img alt="add task" src={PlusIcon} />
      </button>
    ));

  const bottomAction = isLoading ? (
    <Skeleton width={52} height={11} />
  ) : (
    <Button className="!p-0 min-h-8" variant="plain">
      <Link
        to={{ pathname: viewAllPath, search: location.search }}
        className="w-full h-full flex items-center justify-center"
      >
        View all
      </Link>
    </Button>
  );
  return (
    <InfoCard
      title="To Do"
      headerAction={headerAction}
      loading={isLoading}
      bottomAction={bottomAction}
      classes={{ bottomAction: '!py-2' }}
    >
      {isLoading && <UpcomingTasksSkeleton />}

      {!isLoading && !tasks.length && (
        <div className="w-full h-full flex items-center justify-center py-2.5">
          You don't have upcoming tasks yet
        </div>
      )}
      <div className="h-min-[100px] break-all">
        {!isLoading && !!tasks.length && (
          <div className="flex flex-col gap-6">
            {tasks.map(task => {
              const isTaskOverdueSoon = checkIfTaskOverdueSoon(task);
              const overdueResultsCount = (task?.task_results || []).filter(
                ({ result }) => result === TaskResult.Due,
              ).length;
              return (
                <div key={task.id} className="flex w-full">
                  <div className="flex flex-col gap-2 justify-between w-full">
                    <div>
                      {!isUserAdmin && (
                        <Link
                          to={{
                            pathname: commonPaths.dynamic.task(task.id),
                            search: location.search,
                          }}
                          state={{ returnUrl: location.pathname }}
                          className="text-sm hover:underline"
                        >
                          {task.name}
                        </Link>
                      )}
                      {isUserAdmin &&
                        checkIfTaskAssignedToUser(currentUserData, task?.users || []) && (
                          <Link
                            to={{
                              pathname: commonPaths.dynamic.task(task.id),
                              search: location.search,
                            }}
                            state={{ returnUrl: location.pathname }}
                            className="text-sm hover:underline"
                          >
                            {task.name}
                          </Link>
                        )}
                      {isUserAdmin &&
                        !checkIfTaskAssignedToUser(currentUserData, task?.users || []) && (
                          <span
                            className="text-sm hover:underline cursor-pointer"
                            onClick={handleOpenUpdateTaskModal.bind(null, task)}
                          >
                            {task.name}
                          </span>
                        )}
                    </div>
                    <div className="text-[#737373] text-xs">
                      Due date:{' '}
                      {dayjs(normalizeTaskDueDate(task.first_due_date)).format('DD-MM-YYYY, HH:mm')}
                    </div>
                  </div>
                  <div className="flex gap-2 pr-2 shrink-0 items-center">
                    {isUserAdmin &&
                      checkIfTaskAssignedToUser(currentUserData, task?.users || []) && (
                        <Badge type="info">My task</Badge>
                      )}
                    {isTaskOverdueSoon && <Badge type="warning">Overdue soon</Badge>}
                    {!!overdueResultsCount && <OverdueTaskBadge count={overdueResultsCount} />}
                  </div>
                  <UsersAvatarsSet users={task.users || []} className="self-center" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </InfoCard>
  );
};

export default UpcomingTasks;
